/**
 * Style File Overview — the lean reference screen (garment Increment 1, M2).
 *
 * The "file" itself: identity (buyer, PO pill, dates), the colour × size
 * ratio grid as part of its own description, and its own activity history —
 * no production process detail and no duplicated order paperwork. A style
 * created from an order links straight out to that order (`linkedOrder`,
 * ADR-0044) instead of re-showing its PO fields here; a from-scratch style
 * (no linked order) still captures its own PO inline since there's no order
 * to point at. Each process (costing, sampling, T&A, materials, production,
 * QC, dispatch) lives on its own screen, reached from the "Work on this
 * style" rail — not a tab bar and not a button row.
 *
 * Three rounds of product feedback shaped this: the old 10-tab
 * `style-detail-page.tsx` was "very complicated"; a lateral tab bar and a
 * hero action row were still too much ("anything can be a link from this
 * definition file"); and the embedded Order section duplicated a screen
 * that already exists at `/merchandising/orders/$orderId`.
 *
 * @see prototype/garment/style-file.html — V1.1 Studio Slate baseline (§sec-order)
 * @see docs/product/garment-os-screen-build-plan.md §A
 */
import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Ban,
  Copy,
  Image as ImageIcon,
  Layers,
  Package,
  ShieldCheck,
  ShoppingBag,
  Ship,
  Calculator,
  Calendar,
  Check,
  Factory,
} from "lucide-react";
import { Alert, Button, ConfirmDialog, PromptDialog, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { useModule } from "../../../lib/modules.js";
import { formatDate, formatDateTime, bpToPercent, paiseToMoney } from "../../../lib/format.js";
import { styleStatusLabel, type StyleStatus } from "../lib/status.js";
import { garmentTypeLabel } from "../lib/garment-type.js";
import { GarmentFlat } from "./design-studio/garment-flat.js";
import { StyleCockpitChrome, SectionCard } from "./style-cockpit-chrome.js";
import type { samples as samplesContract, dispatch as dispatchContract } from "@flint/api-contract";
import { SamplingSummaryCard } from "./sampling-summary-card.js";
import { CostingSummaryCard } from "./costing-summary-card.js";
import { SourcingSummaryCard } from "./sourcing-summary-card.js";
import { PpGateCard } from "./pp-gate-card.js";

type SamplesWire = samplesContract.SamplesViewWire;
type DispatchWire = dispatchContract.DispatchViewWire;

/**
 * The other 5 process screens + Visual — the rail's link hub, replacing the
 * old tab bar. Sourcing (formerly "Materials"), Costing & BOM, and Sampling
 * are rendered separately, just above this list, since they need a live
 * status badge fed by their own tRPC queries.
 */
const WORK_LINKS = [
  { to: "/merchandising/styles/$styleId/timeline", label: "Time & Action", icon: Calendar },
  { to: "/production/styles/$styleId/production", label: "Production", icon: Factory },
  { to: "/quality/styles/$styleId/qc", label: "Quality Control", icon: ShieldCheck },
  { to: "/merchandising/styles/$styleId/dispatch", label: "Dispatch", icon: Ship },
  { to: "/merchandising/styles/$styleId/visual", label: "Visual style file", icon: ImageIcon },
] as const;

/** Statuses at or past the PP gate — cutting is cleared. */
const PAST_PP_GATE = new Set(["pp_approved", "in_production", "shipped", "closed"]);

export function StyleOverviewPage({ styleId }: { styleId: string }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  // Design Studio (visual/design/tech-pack) is a 1.0-withheld module (ADR-0046).
  // Hide its entry points so they don't dead-end at the route guard's redirect.
  const hasDesignStudio = useModule("design-studio");

  const query = trpc.styles.get.useQuery({ id: styleId });
  const audit = trpc.auditLog.list.useQuery({ entityType: "garment_style", entityId: styleId, limit: 10 });
  // Finished-vs-ordered progress — reuses the F0 WIP read-model (garment.production
  // entitlement; a Style-Files-only tenant simply won't have it, so this fails soft).
  const wip = trpc.wip.forStyle.useQuery({ styleId });
  // Sourcing rail status — same read-model the Sourcing screen itself renders.
  const materials = trpc.materials.getForStyle.useQuery({ styleId });
  // Quoted margin — same cost sheet the Costing screen reads; only meaningful
  // once locked (mirrors `style.fobPricePaise`, set at the same lock step).
  const costing = trpc.costing.get.useQuery({ styleId });
  // Sampling rail status — same read-model the Sampling screen renders.
  const samples = trpc.samples.getForStyle.useQuery({ styleId });
  // Dispatch rail status — surfaces draft/confirmed + QC-gate blocker.
  const dispatch = trpc.dispatch.getForStyle.useQuery({ styleId });

  const invalidate = async () => {
    await Promise.all([
      utils.styles.get.invalidate({ id: styleId }),
      utils.styles.list.invalidate(),
      utils.auditLog.list.invalidate(),
    ]);
  };

  const transition = trpc.styles.transitionStatus.useMutation({
    onSuccess: async () => { await invalidate(); setCancelOpen(false); toast.success("Style cancelled"); },
    onError: (e: { message: string }) => toast.error("Couldn't cancel", { description: e.message }),
  });
  const clone = trpc.styles.clone.useMutation({
    onSuccess: async (res: { id: string }) => {
      await utils.styles.list.invalidate();
      toast.success("Re-order draft created");
      void navigate({ to: "/merchandising/styles/$styleId/overview", params: { styleId: res.id } });
    },
    onError: (e: { message: string }) => toast.error("Couldn't clone", { description: e.message }),
  });

  const busy = transition.isPending || clone.isPending;

  if (query.isLoading) return <p className="p-6 text-sm text-muted">Loading style file…</p>;
  if (query.error || !query.data) {
    return (
      <div className="p-6">
        <Alert intent="bad" title="Couldn't load style file" description={query.error?.message ?? "Not found."} />
      </div>
    );
  }

  const style = query.data;
  const status = style.status as StyleStatus;
  const grid = style.grid;
  const qtyAt = new Map<string, number>();
  for (const l of style.lines) qtyAt.set(`${l.colour}|${l.size}`, l.qty);
  const hexOf = new Map<string, string | null>();
  for (const c of grid.byColour) hexOf.set(c.colour, c.colourHex);
  const daysToExFactory = style.exFactoryDate ? daysFromToday(style.exFactoryDate) : null;
  const hasPo = !!style.poNumber && !!style.poDate;
  const quotedFob = style.fobPricePaise ? paiseToMoney(style.fobPricePaise, style.currency) : null;
  const quotedMargin = quotedFob && costing.data?.marginBp != null ? bpToPercent(costing.data.marginBp) : null;

  // The BOM is the hinge: sourcing, sampling and the PP gate are all meaningless
  // without it, and it only exists once the costing locks.
  const bomReady = materials.data?.hasBom ?? false;

  const finishedQty = wip.data?.finishedTotal ?? 0;
  const finishedPct =
    grid.totalQty > 0 ? Math.min(100, Math.round((finishedQty / grid.totalQty) * 100)) : 0;

  const onClone = () => setReorderOpen(true);

  return (
    <StyleCockpitChrome styleId={styleId} style={style}>
      {/* ─────────── 2-COLUMN LAYOUT — the whole file, top to bottom. Left: pure reference content. Right: every link/action, sticky. ─────────── */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-w-0 flex-col gap-4">
          {/* ── The order card: bigger garment visual, identity, and total/finished progress ── */}
          <section className="relative overflow-hidden rounded-lg border border-line bg-paper shadow-sm">
            {daysToExFactory !== null ? (
              <div
                className={`absolute right-5 top-4 text-sm font-bold ${
                  daysToExFactory < 5 ? "text-bad" : daysToExFactory < 10 ? "text-warn" : "text-ink"
                }`}
              >
                {daysToExFactory < 0 ? `${Math.abs(daysToExFactory)} days overdue` : `${daysToExFactory} days to ex-factory`}
              </div>
            ) : null}
            <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-start">
              {/* Left column — the garment flat, bigger, no border. Links into the
                  visual style file only when Design Studio is entitled (ADR-0046);
                  otherwise the flat renders as a plain, non-interactive preview. */}
              <div className="flex w-56 shrink-0 flex-col items-center gap-3 self-center sm:w-64 sm:self-start">
                {hasDesignStudio ? (
                  <Link
                    to="/merchandising/styles/$styleId/visual"
                    params={{ styleId }}
                    title="Open the visual style file"
                    className="group flex w-full flex-col items-center gap-3 rounded-md bg-paper text-muted transition hover:text-sky-600"
                  >
                    <div className="w-44">
                      <GarmentFlat garmentType={style.garmentType} view="front" viewBox="0 40 400 420" />
                    </div>
                    <span className="rounded-full bg-raised px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-muted transition group-hover:bg-sky-50 group-hover:text-sky-700">
                      Open visual
                    </span>
                  </Link>
                ) : (
                  <div className="flex w-full flex-col items-center gap-3 rounded-md bg-paper text-muted">
                    <div className="w-44">
                      <GarmentFlat garmentType={style.garmentType} view="front" viewBox="0 40 400 420" />
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted">Style code</div>
                  <div className="mt-0.5 text-xl font-bold text-muted">{style.styleRef}</div>
                </div>
              </div>

              {/* Right of the diagram — identity + colour × size. */}
              <div className="min-w-0 flex-1">
                {/* Identity — name, then its description, then the tags below that. */}
                <h2 className="text-xl font-bold tracking-tight text-ink">{style.name}</h2>
                <p className="mt-1 text-sm text-muted">
                  {garmentTypeLabel(style.garmentType)} · {style.buyerName}
                  {style.parentStyleRef ? ` · re-order of ${style.parentStyleRef}` : ""} · Created {formatDate(style.createdAt)}
                </p>
                <p className="mt-1 text-sm text-muted">
                  {style.buyerName}
                  {[style.buyerCity, style.buyerState].filter(Boolean).length > 0
                    ? ` · ${[style.buyerCity, style.buyerState].filter(Boolean).join(", ")}`
                    : ""}
                  {style.buyerGstin ? ` · GSTIN ${style.buyerGstin}` : ""}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {style.jobNo ? <Pill tone="violet">{style.jobNo}</Pill> : null}
                  {hasPo ? <Pill tone="info">PO {style.poNumber}</Pill> : null}
                  <Pill tone={status === "draft" ? "muted" : "ok"} dot>{styleStatusLabel(status)}</Pill>
                  {style.linkedOrder ? <Pill tone="rose">{style.linkedOrder.orderNo}</Pill> : null}
                </div>

                {/* An order-linked style needs no PO data of its own — it advances
                    automatically the moment the linked order is accepted. */}
                {style.linkedOrder && status === "draft" ? (
                  <div className="mt-3 flex items-center justify-between gap-2 rounded-sm border border-line bg-bg p-3">
                    <p className="text-xs text-ink-2">
                      Waiting on order acceptance — moves to Confirmed automatically once{" "}
                      <span className="font-mono font-semibold">{style.linkedOrder.orderNo}</span> is accepted.
                    </p>
                    <Link
                      to="/merchandising/orders/$orderId"
                      params={{ orderId: style.linkedOrder.id }}
                      className="flex shrink-0 items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-800"
                    >
                      View order <ArrowRight size={12} />
                    </Link>
                  </div>
                ) : null}

                {/* Colour × size — same right-hand column as identity, not a separate full-width section. */}
                {grid.colours.length > 0 ? (
                  <div className="mt-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Colour × size</p>
                    <div className="inline-block max-w-full overflow-hidden rounded-md border border-line align-top">
                      <div className="overflow-x-auto">
                        <table className="border-collapse text-sm">
                          <thead>
                            <tr className="border-b border-line bg-raised/60 text-xs uppercase tracking-wide text-muted">
                              <th className="py-2 pl-3.5 pr-3 text-left font-medium">Colour</th>
                              {grid.sizes.map((s) => <th key={s} className="border-l border-line px-3 py-2 text-right font-medium">{s}</th>)}
                              <th className="border-l border-line py-2 pl-3 pr-3.5 text-right font-medium">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grid.colours.map((colour) => (
                              <tr key={colour} className="border-b border-line last:border-0">
                                <td className="py-2.5 pl-3.5 pr-3">
                                  <span className="inline-flex items-center gap-2">
                                    <span className="h-3.5 w-3.5 shrink-0 rounded-[3px] border border-line-2" style={{ background: hexOf.get(colour) ?? "transparent" }} aria-hidden />
                                    <span className="font-medium text-ink">{colour}</span>
                                  </span>
                                </td>
                                {grid.sizes.map((s) => <td key={s} className="border-l border-line px-3 py-2.5 text-right font-mono text-ink-2">{qtyAt.get(`${colour}|${s}`) || "—"}</td>)}
                                <td className="border-l border-line py-2.5 pl-3 pr-3.5 text-right font-mono font-semibold text-ink">{(grid.byColour.find((c) => c.colour === colour)?.qty ?? 0).toLocaleString("en-IN")}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-line bg-raised font-semibold text-ink">
                              <td className="py-2.5 pl-3.5 pr-3 text-left">Total</td>
                              {grid.sizes.map((s) => <td key={s} className="border-l border-line px-3 py-2.5 text-right font-mono text-ink-2">{(grid.bySize.find((x) => x.size === s)?.qty ?? 0).toLocaleString("en-IN")}</td>)}
                              <td className="border-l border-line py-2.5 pl-3 pr-3.5 text-right font-mono">{grid.totalQty.toLocaleString("en-IN")} pcs</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* The process timeline — attached to this same card, per section. */}
            <div className="border-t border-line px-5 py-3">
              <StageTimeline status={status} />
            </div>
          </section>

          {/* ── Costing + BOM — one row, split: the cost build-up and what it says
               to buy. Hidden entirely when no cost sheet exists: an empty card
               reading "No cost sheet yet · Go to Costing" says exactly what the
               banner below it says, only quieter and twice. ── */}
          {costing.data ? (
            <CostingSummaryCard
              styleId={styleId}
              costing={costing.data}
              currency={style.currency}
              loading={costing.isLoading}
            />
          ) : null}

          {/* ── Everything downstream of the costing hangs off the BOM, which the
               costing lock generates. Before that, Sourcing / Sampling / PP Gate
               are three cards that can only say "nothing yet" — noise in place of
               a next step. One banner replaces all three and names the step. ── */}
          {bomReady ? (
            <>
              {/* ── Sourcing — before Sampling, because the sample is cut from real
                   material: nothing can be sampled until fabric and trims are in
                   hand (confirmed with a customer, 2026-07-19). The card is visited
                   twice in a style's life — order here first, then come back to
                   issue to cutting once PP clears. ── */}
              <SourcingSummaryCard
                styleId={styleId}
                materials={materials.data}
                loading={materials.isLoading}
                ppApproved={PAST_PP_GATE.has(status)}
              />

              {/* ── Sampling — status, photo tiles, comments (read-only glance). ── */}
              <SamplingSummaryCard styleId={styleId} />

              {/* ── PP Gate — the pre-production sign-off. Consumes the approved
                   sample; unlocks issuing material to bulk cutting. ── */}
              <PpGateCard styleId={styleId} />
            </>
          ) : materials.isLoading || costing.isLoading ? null : (
            <StartWithCostingBanner styleId={styleId} hasDraft={!!costing.data} />
          )}

          {/* Activity & audit — folded into Overview (it's the file's own history, not a process) */}
          <SectionCard title="Activity & audit" meta="Append-only · this style's events" id="sec-audit">
            {audit.isLoading ? (
              <p className="text-sm text-muted">Loading activity…</p>
            ) : (audit.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-muted">No activity recorded yet.</p>
            ) : (
              <ol className="flex flex-col">
                {audit.data!.items.map((ev, i) => {
                  const isLast = i === audit.data!.items.length - 1;
                  const warn = ev.severity === "warn" || ev.severity === "rose";
                  return (
                    <li key={ev.id} className="flex gap-3">
                      <div className="flex flex-col items-center self-stretch">
                        <span aria-hidden className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ring-4 ring-paper ${warn ? "bg-warn" : "bg-ok"}`} />
                        {!isLast ? <span aria-hidden className="w-px grow bg-line" /> : null}
                      </div>
                      <div className={`min-w-0 flex-1 ${isLast ? "pb-0.5" : "pb-4"}`}>
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-sm text-ink-2">
                            <b className="font-semibold text-ink">{ev.actorName ?? actorLabel(ev.actorType)}</b> {verbPhrase(ev.verb)}
                          </span>
                          <span className="text-xs text-muted">· {formatDateTime(ev.at)}</span>
                        </div>
                        {auditMetaLine(ev.metadata) ? <div className="mt-0.5 text-xs text-muted">{auditMetaLine(ev.metadata)}</div> : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </SectionCard>
        </div>

        {/* ── Right sidebar — every link and action lives here, pinned while the file scrolls (matches every other detail page in this app: po-detail-page.tsx, garment-order-detail-page.tsx). ── */}
        <aside className="w-full">
        <div className="sticky top-20 flex flex-col gap-4">
          {/* ── At a glance — the four figures anyone opening this file wants
               first: how many, what it sells for, when it ships, what it earns.
               One card rather than two: "Total pieces" and "Key numbers" were
               the same question asked twice, and splitting them put a card
               boundary between the quantity and the price of that quantity. ── */}
          <RailCard title="At a glance">
            {/* Hero: the order size, with real progress against it. */}
            <div className="flex flex-col items-center pb-4 text-center">
              <div className="text-3xl font-bold leading-none tracking-tight text-ink">
                {grid.totalQty.toLocaleString("en-IN")}
              </div>
              <div className="mt-1 text-xs uppercase tracking-wide text-muted">pieces ordered</div>

              {/* The bar earns its place: "405 finished" means little without
                  the proportion it represents. */}
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-bg">
                <div
                  className="h-full rounded-full bg-ok transition-[width]"
                  style={{ width: `${finishedPct}%` }}
                />
              </div>
              <div className="mt-1.5 text-xs text-muted">
                {finishedQty > 0 ? (
                  <>
                    <span className="font-semibold text-ok">{finishedQty.toLocaleString("en-IN")}</span> finished ·{" "}
                    <span className="font-semibold text-ink">{finishedPct}%</span>
                  </>
                ) : (
                  "Production not started"
                )}
              </div>
            </div>

            <dl className="flex flex-col divide-y divide-line border-t border-line">
              <RailStat
                label="Quoted FOB"
                value={quotedFob ? `${quotedFob}/pc` : "—"}
                sub={quotedFob ? "Locked at costing" : "Sets at costing"}
                muted={!quotedFob}
              />
              <RailStat
                label="Ex-factory"
                value={style.exFactoryDate ? formatDate(style.exFactoryDate) : "—"}
                sub={daysToExFactory !== null ? `${daysToExFactory} days from today` : "Not set"}
                warn={daysToExFactory !== null && daysToExFactory < 14}
              />
              <RailStat
                label="Margin"
                value={quotedMargin ? `${quotedMargin}%` : "—"}
                sub={quotedMargin ? "Locked at costing" : "Sets at costing"}
                muted={!quotedMargin}
              />
            </dl>
          </RailCard>

          {style.linkedOrder ? (
            <RailCard title="From order">
              <Link
                to="/merchandising/orders/$orderId"
                params={{ orderId: style.linkedOrder.id }}
                className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm font-semibold text-sky-700 transition hover:bg-bg"
              >
                {style.linkedOrder.orderNo} <ArrowRight size={13} />
              </Link>
            </RailCard>
          ) : null}

          <RailCard title="Work on this style">
            <ul className="flex flex-col">
              <li>
                <Link
                  to="/merchandising/styles/$styleId/materials"
                  params={{ styleId }}
                  className="flex items-center justify-between gap-2.5 rounded-md px-1.5 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg hover:text-ink"
                >
                  <span className="flex items-center gap-2.5">
                    <Package size={15} className="shrink-0 text-muted" />
                    Sourcing
                  </span>
                  {materials.data?.hasBom ? (
                    <SourcingBadge lines={materials.data.lines} />
                  ) : (
                    <span className="text-xs text-muted">Not started</span>
                  )}
                </Link>
              </li>
              <li>
                <Link
                  to="/merchandising/styles/$styleId/costing"
                  params={{ styleId }}
                  className="flex items-center justify-between gap-2.5 rounded-md px-1.5 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg hover:text-ink"
                >
                  <span className="flex items-center gap-2.5">
                    <ShoppingBag size={15} className="shrink-0 text-muted" />
                    Costing &amp; BOM
                  </span>
                  <CostingBadge status={costing.data?.status} />
                </Link>
              </li>
              <li>
                <Link
                  to="/sampling/styles/$styleId/samples"
                  params={{ styleId }}
                  className="flex items-center justify-between gap-2.5 rounded-md px-1.5 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg hover:text-ink"
                >
                  <span className="flex items-center gap-2.5">
                    <Layers size={15} className="shrink-0 text-muted" />
                    Sampling
                  </span>
                  <SamplingBadge data={samples.data} />
                </Link>
              </li>
              {WORK_LINKS.filter(
                (l) => hasDesignStudio || l.to !== "/merchandising/styles/$styleId/visual",
              ).map((l) => {
                const isDispatch = l.to === "/merchandising/styles/$styleId/dispatch";
                return (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      params={{ styleId }}
                      className={`flex items-center gap-2.5 rounded-md px-1.5 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg hover:text-ink${
                        isDispatch ? " justify-between" : ""
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <l.icon size={15} className="shrink-0 text-muted" />
                        {l.label}
                      </span>
                      {isDispatch ? <DispatchBadge data={dispatch.data} /> : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </RailCard>

          <RailCard title="Actions">
            <div className="flex flex-col gap-2">
              <Button intent="ghost" className="justify-start border border-line bg-paper" disabled={busy} onClick={onClone}>
                <Copy size={15} /> Clone &amp; amend
              </Button>
              {status === "draft" ? (
                <Button intent="ghost" className="justify-start border border-line bg-paper text-bad hover:bg-bad-50" disabled={busy} onClick={() => setCancelOpen(true)}>
                  <Ban size={15} /> Cancel
                </Button>
              ) : null}
            </div>
          </RailCard>
        </div>
        </aside>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel this style file?"
        description={`${style.styleRef} (${style.name}) will move to cancelled. The row stays as a historical record; you can't un-cancel it.`}
        intent="danger"
        confirmLabel={transition.isPending ? "Cancelling…" : "Cancel style"}
        onConfirm={() => transition.mutate({ id: styleId, status: "cancelled" })}
      />

      <PromptDialog
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        title="Re-order this style"
        description="Clones the style file into a new draft you can amend — the original stays untouched, linked as the parent."
        label="New style code"
        defaultValue={`${style.styleRef}-R1`}
        placeholder="e.g. FT-SH-102-R1"
        confirmLabel="Create re-order"
        pending={clone.isPending}
        onConfirm={(ref) => clone.mutate({ id: styleId, styleRef: ref })}
      />
    </StyleCockpitChrome>
  );
}

/* ── small presentational helpers ─────────────────────────────── */


/** Costing progress for the rail link — mirrors the "Costing locked" style-status pill. */
function CostingBadge({ status }: { status: "draft" | "locked" | undefined }) {
  if (status === "locked") return <Pill tone="ok" dot>Locked</Pill>;
  if (status === "draft") return <Pill tone="muted">In progress</Pill>;
  return <span className="text-xs text-muted">Not started</span>;
}

/**
 * Live sampling status for the rail link — same read-model as the Sampling
 * screen. "Awaiting decision" wins over "Submitted" so the founder scanning
 * the rail sees where their attention is needed (matches the main section's
 * blue "Submitted" pill semantically).
 */
function SamplingBadge({
  data,
}: {
  data: Pick<SamplesWire, "status" | "revisionCount"> | undefined;
}) {
  if (!data || data.revisionCount === 0) {
    return <span className="text-xs text-muted">Not started</span>;
  }
  if (data.status === "approved") return <Pill tone="ok" dot>Approved</Pill>;
  if (data.status === "submitted") return <Pill tone="info" dot>Awaiting decision</Pill>;
  if (data.status === "rejected") return <Pill tone="rose" dot>Rejected</Pill>;
  return <span className="text-xs text-muted">Not started</span>;
}

/** Live dispatch state for the rail link — draft / confirmed / QC-gate blocker. */
function DispatchBadge({ data }: { data: DispatchWire | undefined }) {
  if (!data) return null;
  if (data.dispatch?.status === "confirmed") return <Pill tone="ok" dot>Shipped</Pill>;
  // A QC block can coexist with a draft dispatch (qcBlocked is derived from an
  // unresolved rejection, independent of dispatch status). Surface the blocker —
  // the actionable state — ahead of the informational "Draft".
  if (data.qcBlocked) return <span className="text-xs text-warn">QC gate blocking</span>;
  if (data.dispatch?.status === "draft") return <Pill tone="warn" dot>Draft</Pill>;
  return null;
}

/**
 * Live sourcing progress for the rail link — mirrors the Sourcing screen's own
 * plan derivation.
 *
 * Deliberately tone-coded: this previously rendered every partial state in
 * muted grey, so a style short of fabric looked exactly like one nobody had
 * started. Anything blocking now reads amber.
 */
function SourcingBadge({ lines }: { lines: ReadonlyArray<{ plan: string }> }) {
  const total = lines.length;
  if (total === 0) return null;
  const n = (p: string) => lines.filter((l) => l.plan === p).length;

  if (n("issued") === total) return <Pill tone="ok" dot>All issued</Pill>;

  const toOrder = n("to_source") + n("partly_ready");
  if (toOrder > 0) return <Pill tone="warn" dot>{toOrder} to order</Pill>;

  const waiting = n("awaiting");
  if (waiting > 0) return <Pill tone="info" dot>{waiting} on the way</Pill>;

  return <Pill tone="ok" dot>All ready</Pill>;
}

function Pill({
  children,
  tone,
  dot,
}: {
  children: React.ReactNode;
  tone?: "info" | "ok" | "warn" | "muted" | "violet" | "rose";
  dot?: boolean;
}) {
  const styles =
    tone === "info" ? "border-sky-100 bg-sky-50 text-sky-700"
    : tone === "ok" ? "border-emerald-100 bg-emerald-50 text-emerald-700"
    : tone === "warn" ? "border-amber-100 bg-warn-50 text-warn"
    : tone === "violet" ? "border-violet-100 bg-violet-50 text-violet-700"
    : tone === "rose" ? "border-rose-100 bg-rose-50 text-rose-700"
    : "border-line bg-paper text-ink-2";
  const dotColor =
    tone === "info" ? "bg-sky-600"
    : tone === "ok" ? "bg-emerald-600"
    : tone === "warn" ? "bg-amber-500"
    : tone === "violet" ? "bg-violet-600"
    : tone === "rose" ? "bg-rose-600"
    : "bg-line-2";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold ${styles}`}>
      {dot ? <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} aria-hidden /> : null}
      {children}
    </span>
  );
}

function RailCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted">{title}</h3>
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

/** The process timeline — order → PO → costing → sampling → production → shipped. */
function StageTimeline({ status }: { status: StyleStatus }) {
  if (status === "cancelled") {
    return (
      <div className="rounded-md border border-bad-100 bg-bad-50 px-4 py-2 text-sm font-semibold text-bad">
        This style was cancelled.
      </div>
    );
  }
  const steps: { key: StyleStatus; label: string }[] = [
    { key: "draft", label: "Order" },
    { key: "po_confirmed", label: "Confirmed" },
    { key: "costing_locked", label: "Costing" },
    { key: "pp_approved", label: "Sampling" },
    { key: "in_production", label: "Production" },
    { key: "shipped", label: "Shipped" },
  ];
  const order = ["draft", "po_confirmed", "costing_locked", "pp_approved", "in_production", "shipped", "closed"];
  const currentIdx = status === "closed" ? steps.length - 1 : order.indexOf(status);
  return (
    <ol className="flex items-center overflow-x-auto" aria-label="Style process timeline">
      {steps.map((s, i) => {
        const done = i < currentIdx;
        const current = i === currentIdx;
        return (
          <li key={s.key} className={`flex items-center gap-2 ${i < steps.length - 1 ? "flex-1" : "shrink-0"}`}>
            <span
              className={`flex shrink-0 items-center gap-2 rounded-full py-1 pl-1 pr-3 ${current ? "bg-sky-50" : ""}`}
            >
              <span
                className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${
                  done ? "bg-ok text-paper" : current ? "bg-sky-600 text-paper" : "bg-bg text-muted"
                }`}
              >
                {done ? <Check size={13} /> : i + 1}
              </span>
              <span className={`whitespace-nowrap text-sm font-medium ${current ? "text-ink" : done ? "text-ink-2" : "text-muted"}`}>
                {s.label}
              </span>
            </span>
            {i < steps.length - 1 ? <span className={`h-px flex-1 ${done ? "bg-ok" : "bg-line"}`} aria-hidden /> : null}
          </li>
        );
      })}
    </ol>
  );
}

/** Quoted-FOB formatter — ₹ for INR styles, currency-prefixed decimal otherwise. */
function daysFromToday(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${iso}T00:00:00`);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

function actorLabel(actorType: string): string {
  return actorType === "system" ? "System" : actorType === "ai" ? "Flint AI" : "A teammate";
}

/** Turn an audit verb into a human phrase. */
function verbPhrase(verb: string): string {
  switch (verb) {
    case "created": return "created this style file";
    case "po-details-added": return "captured the buyer PO";
    case "po-confirmed": return "confirmed the order";
    case "status-changed": return "changed the status";
    case "cancelled": return "cancelled the style";
    case "cloned": return "cloned this style for re-order";
    default: return verb.replace(/-/g, " ");
  }
}

function auditMetaLine(meta: Record<string, unknown>): string | null {
  const parts: string[] = [];
  if (typeof meta.poNumber === "string") parts.push(`PO ${meta.poNumber}`);
  if (typeof meta.totalQty === "number") parts.push(`${meta.totalQty.toLocaleString("en-IN")} pcs`);
  if (typeof meta.from === "string" && typeof meta.to === "string") parts.push(`${meta.from} → ${meta.to}`);
  if (typeof meta.parentStyleRef === "string") parts.push(`from ${meta.parentStyleRef}`);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * The one thing to do on a style that hasn't been costed yet.
 *
 * Replaces the Sourcing / Sampling / PP Gate cards while there's no BOM. Those
 * three could only render "nothing yet" three times over, which reads as three
 * broken sections rather than one step not taken.
 */
function StartWithCostingBanner({ styleId, hasDraft }: { styleId: string; hasDraft: boolean }) {
  return (
    <section className="rounded-lg border border-line bg-paper p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-500/10 text-sky-600">
          <Calculator size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-ink">
            {hasDraft ? "Lock the costing to open the rest of this style" : "Start with the costing"}
          </h3>
          <p className="mt-1 text-sm text-muted">
            The cost sheet fixes your quoted price and generates the bill of materials. Sourcing,
            sampling and the PP gate all open up once it's locked.
          </p>
        </div>
        <Button size="lg" asChild>
          <Link to="/merchandising/styles/$styleId/costing" params={{ styleId }}>
            {hasDraft ? "Finish the costing" : "Do the costing"}
            <ArrowRight size={15} />
          </Link>
        </Button>
      </div>
    </section>
  );
}

/** One figure in the rail's "Key numbers" card — label, value, and its caveat. */
function RailStat({
  label, value, sub, muted = false, warn = false,
}: {
  label: string;
  value: string;
  sub: string;
  muted?: boolean;
  warn?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-right leading-tight">
        <div className={`text-sm font-bold ${warn ? "text-warn" : muted ? "text-muted" : "text-ink"}`}>{value}</div>
        <div className="mt-0.5 text-xs text-muted">{sub}</div>
      </dd>
    </div>
  );
}
